import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Accordion,
  Button,
  Form,
  Grid,
  Icon,
  List,
  Segment,
} from "semantic-ui-react";
import sortBy from "lodash/sortBy";
import moment from "moment-timezone";
import PropTypes from "prop-types";
import Dropzone from "react-dropzone";

import CustomAttachmentForm from "routes/campaign/forms/CustomAttachmentForm";
import EmailableContactsModal from "routes/campaign/components/EmailableContactsModal";
import EmailDeliveryTime from "routes/campaign/forms/EmailDeliveryTime";
import RuvixxForm from "components/RuvixxForm";
import OutreachFiltersForm from "components/OutreachFiltersForm";
import EmailScheduleSendOptionForm from "routes/campaign/forms/EmailScheduleSendOptionForm";
import ScheduleConfirmationModal from "routes/campaign/components/ScheduleConfirmationModal";
import { getUserTimeZone } from "components/helpers";

import CampaignService from "services/Campaign";
import CampaignScheduleService from "services/CampaignScheduleService";
import EmailTemplateService from "services/EmailTemplate";
import EmailFooterService from "services/EmailFooters";
import EmailSignatureService from "services/EmailSignatures";
import CustomEmailDomainsService from "services/CustomEmailDomains";
import TagService from "services/Tag";
import UploadManager from "components/UploadManager";
import UploadProgressRing from "components/UploadProgressRing";
import SmtpAccountService from "services/SmtpAccounts";
import EmailProviderService from "services/EmailProviders";
import UserService from "services/User";
import CONSTANTS from "constants/Constants";
import { v4 as uuidv4 } from "uuid";
import ConstantContactAccountsService from "services/ConstantContactAccounts";
import Validate from "components/Validate";
import EmailAttachmentsService from "services/EmailAttachments";

const templateDisabled = (template, provider) => {
  const { provider_list: providerList } = template.info;
  if (!provider) return false;
  if (!providerList) return false;
  let disabled;
  switch (provider) {
    case CONSTANTS.EMAIL_SERVICES.MAILGUN:
      disabled = !providerList.mailgun_sendgrid;
      break;
    case CONSTANTS.EMAIL_SERVICES.CONSTANT_CONTACT:
      disabled = !providerList.constant_contact;
      break;
    case CONSTANTS.EMAIL_SERVICES.SMTP:
      disabled = !providerList.smtp;
      break;
    default:
      disabled = false;
  }
  return disabled;
};

const convertLocalDisplayToUTC = (localMoment, timezone) => {
  if (!localMoment) return null;

  const displayedTime = localMoment.format("YYYY-MM-DD HH:mm:ss");
  return moment.tz(displayedTime, timezone).utc();
};

const adjustDisplayTimeForTimezoneChange = (
  currentDisplayMoment,
  oldTimezone,
  newTimezone
) => {
  if (!currentDisplayMoment) return null;

  // Get the displayed time string and interpret it in the old timezone
  const displayedTime = currentDisplayMoment.format("YYYY-MM-DD HH:mm:ss");
  const utcTime = moment.tz(displayedTime, oldTimezone).utc();

  // Convert to new timezone and create a UTC moment with that local time value
  const newDisplayTime = utcTime.tz(newTimezone);
  return moment.utc(newDisplayTime.format("YYYY-MM-DD HH:mm:ss"));
};

const CampaignScheduleForm = ({
  campaignId,
  onClose,
  onSuccess,
  scheduleId: initialScheduleId,
  isClone,
}) => {
  const [uploadManager, setUploadManager] = useState(undefined);
  const [scheduleId, setScheduleId] = useState(initialScheduleId);
  const [scheduleName, setScheduleName] = useState("");
  const [status, setStatus] = useState(false);
  const [sendNow, setSendNow] = useState(true);
  const [emailDeliveryTime, setEmailDeliveryTime] = useState(null);
  const [timeZone, setTimeZone] = useState(getUserTimeZone());
  const [timeZones, setTimeZones] = useState([]);
  const [emailTemplateId, setEmailTemplateId] = useState(undefined);
  const [conditions, setConditions] = useState([]);
  const [customAttachments, setCustomAttachments] = useState([]);
  const [campaignStatusOptions, setCampaignStatusOptions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [emailTemplateOptions, setEmailTemplateOptions] = useState([]);
  const [files, setFiles] = useState([]);
  const [entityTagOptions, setEntityTagOptions] = useState([]);
  const [callTagOptions, setCallTagOptions] = useState([]);
  const [contactTagOptions, setContactTagOptions] = useState([]);
  const [senderName, setSenderName] = useState("noreply");
  const [fromAddress, setFromAddress] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitErrors, setSubmitErrors] = useState({});
  const errorMessages = { ...fieldErrors, ...submitErrors };

  const [ccMenuOpen, setCcMenuOpen] = useState(false);
  const [ccAccountManagers, setCcAccountManagers] = useState(false);
  const [ccExternalAccountManagers, setCcExternalAccountManagers] =
    useState(false);
  const [fromDomain, setFromDomain] = useState("email.domain.com");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingToAllContacts, setIsSendingToAllContacts] = useState(false);
  const [validateDisabled, setValidateDisabled] = useState(true);
  const [providerIsSMTP, setProviderIsSMTP] = useState(false);
  const [providerIsConstantContact, setProviderIsConstantContact] =
    useState(false);
  const [providerIsMailgun, setProviderIsMailgun] = useState(false);
  const [constantContactAccounts, setConstantContactAccounts] = useState([]);
  const [constantContactAccountId, setConstantContactAccountId] =
    useState(null);
  const [provider, setProvider] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [smtpOptions, setSmtpOptions] = useState([]);
  const [smtp_config_id, setSmtpConfigId] = useState(undefined);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [emailServices, setEmailServices] = useState([]);
  const [footerOptions, setFooterOptions] = useState([]);
  const [footerId, setFooterId] = useState(null);
  const [signatureOptions, setSignatureOptions] = useState([]);
  const [signatureId, setSignatureId] = useState(null);
  const [mailgunDomainSelected, setMailgunDomainSelected] = useState(null);
  const [users, setUsers] = useState([]);
  const [checkFromField, setCheckFromField] = useState(false);
  const [mailgunDomainOptions, setMailgunDomainOptions] = useState([]);
  const [sendToAllEmailAddresses, setSendToAllEmailAddresses] = useState(false);
  const [emailAttachmentOptions, setEmailAttachmentOptions] = useState([]);
  const [selectedEmailAttachments, setSelectedEmailAttachments] = useState([]);
  const [runOnce, setRunOnce] = useState(false);
  const [runUntil, setRunUntil] = useState(false);
  const [runContinuously, setRunContinuously] = useState(true);
  const [runDateLimit, setRunDateLimit] = useState(null);
  const [runDateLimitTimeZone, setRunDateLimitTimeZone] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const userOptions = useMemo(() => {
    return users.map(user => ({
      key: user.id,
      value: user.id,
      text: user.full_name,
    }));
  }, [users]);

  const setupTimezones = async () => {
    const timeZonesNames = moment.tz.names();
    const usPacificNewIdx = timeZonesNames.indexOf("US/Pacific-New");
    if (usPacificNewIdx !== -1) timeZonesNames.splice(usPacificNewIdx, 1);
    const options = timeZonesNames.map(zone => {
      let offset = moment.tz(zone).format("Z");
      return { text: zone + " (" + offset + ")", key: zone, value: zone };
    });
    setTimeZones(options);
  };

  const fetchEmailServices = async () => {
    const emailServices = await EmailProviderService.getEmailProviders(
      true,
      true
    );
    setEmailServices(emailServices);
  };

  const fetchEmailServicesOptions = async provider => {
    switch (provider) {
      case CONSTANTS.EMAIL_SERVICES.SMTP:
        await fetchSmtpDetails();
        break;
      case CONSTANTS.EMAIL_SERVICES.CONSTANT_CONTACT:
        await fetchConstantContactAccounts();
        break;
      case CONSTANTS.EMAIL_SERVICES.MAILGUN:
        await fetchMailgunDomains();
        break;
      default:
        break;
    }
  };

  const fetchSmtpDetails = async () => {
    let smtpOptions = await SmtpAccountService.getSmtpAccounts();
    smtpOptions = smtpOptions.map(({ id, return_addr }) => ({
      key: id,
      text: return_addr,
      value: id,
    }));
    setSmtpOptions(smtpOptions);
  };

  const handleAccountChange = (event, { value }) => {
    if (!value) {
      return;
    }
    const smtpAccount = smtpOptions
      .filter(opt => opt.value === value)
      .map(({ value, text }) => ({
        id: value,
        return_addr: text,
      }))[0];
    setSmtpConfigId(value);
    setFromAddress(smtpAccount.return_addr);
  };

  const fetchConstantContactAccounts = async () => {
    const accounts = await ConstantContactAccountsService.getForFilters();
    let accountOptions = accounts.map(account => {
      const authenticated = account.authentication_status === "authenticated";
      return {
        text: account.name + (authenticated ? "" : " (Not Authenticated)"),
        key: account.id,
        value: account.id,
        disabled: !authenticated,
      };
    });
    setConstantContactAccounts(accountOptions);
  };

  const fetchMailgunDomains = async () => {
    const domains = await CustomEmailDomainsService.getVerifiedDomains();
    const domainOptions = domains.map(domain => ({
      key: domain.id,
      value: domain.name,
      text: domain.name,
    }));
    const defaultDomain = domains.find(domain => domain.is_default)?.name;

    return {
      domainOptions,
      defaultDomain,
    };
  };

  const fetchEmailAttachments = async () => {
    try {
      const attachments =
        await EmailAttachmentsService.getEmailAttachmentsForFilters();
      const attachmentOptions = attachments.map(attachment => ({
        key: attachment.id,
        value: attachment.id,
        text: attachment.name,
        file_name: attachment.file_name,
        icon: attachment.is_graphical ? "paint brush" : "file",
      }));
      setEmailAttachmentOptions(attachmentOptions);
    } catch (error) {
      console.error("Error fetching email attachments:", error);
      setEmailAttachmentOptions([]);
    }
  };

  const fetchSchedule = async (campaignId, scheduleId, isClone = false) => {
    const schedule = await CampaignScheduleService.getSchedule(
      campaignId,
      scheduleId
    );

    const isMailgun =
      schedule.email_service === CONSTANTS.EMAIL_SERVICES.MAILGUN;

    const isConstantContact =
      schedule.email_service === CONSTANTS.EMAIL_SERVICES.CONSTANT_CONTACT;

    const isConstantContactOrSMTP =
      isConstantContact ||
      schedule.email_service === CONSTANTS.EMAIL_SERVICES.SMTP;
    let fromAddress = schedule.info.from_address;
    let mailgunDomainSelected = null;
    let mailgunDomainOptions = [];

    if (isMailgun) {
      const { domainOptions, defaultDomain } = await fetchMailgunDomains();
      mailgunDomainOptions = domainOptions;

      if (fromAddress && fromAddress.includes("@")) {
        [fromAddress, mailgunDomainSelected] = fromAddress.split("@");
      }

      if (
        !mailgunDomainSelected ||
        !mailgunDomainOptions.find(opt => opt.value === mailgunDomainSelected)
      ) {
        mailgunDomainSelected = defaultDomain;
      }
    }

    const clonedConditions = isClone
      ? [...(schedule.conditions || [])]
      : schedule.conditions || [];
    const shouldSendToAllContacts = clonedConditions.length === 0;
    const emailService = schedule.email_service || "";

    setProvider(emailService);
    if (emailService) {
      await fetchEmailServicesOptions(emailService);
    }
    setScheduleId(isClone ? undefined : scheduleId);
    setSendNow(isConstantContact ? false : schedule.info.send_now);
    setEmailDeliveryTime(
      // A Constant Contact schedule that has not been sent has no delivery
      // time; keep it empty rather than defaulting to today, or saving the
      // form unchanged would record a date the email never went out on.
      isConstantContact && !schedule.email_delivery_time
        ? null
        : getEmailDeliveryTime(schedule.email_delivery_time, schedule.time_zone)
    );
    setTimeZone(schedule.time_zone || "UTC");
    setSenderName(schedule.sender_name || "noreply");
    setFromAddress(fromAddress || "");
    setCc(schedule.info.cc || "");
    setBcc(schedule.info.bcc || "");
    setCcAccountManagers(schedule.info.cc_account_managers || false);
    setCcExternalAccountManagers(
      schedule.info.cc_external_account_managers || false
    );
    setFromDomain("email.domain.com");
    setStatus(isClone ? false : schedule.status || false);
    setEmailTemplateId(schedule.email_template_id || undefined);
    setFooterId(schedule.info.footer_id);
    setSignatureId(schedule.info.signature_id);
    setConditions(clonedConditions);
    setSendToAllEmailAddresses(schedule.info.send_to_all_email_addresses);

    if (schedule.info?.schedule_send_option) {
      const options = schedule.info.schedule_send_option;
      setRunOnce(options.run_once || false);
      setRunContinuously(options.run_continuously !== false);
      setRunUntil(options.run_until || false);

      if (isClone) {
        setRunDateLimit(null);
        setRunDateLimitTimeZone(null);
      } else {
        const effectiveTimeZone =
          options.run_date_limit_time_zone || schedule.time_zone;
        setRunDateLimit(
          getEmailDeliveryTime(options.run_date_limit, effectiveTimeZone)
        );
        setRunDateLimitTimeZone(effectiveTimeZone);
      }
    } else {
      setRunOnce(false);
      setRunContinuously(true);
      setRunUntil(false);
      setRunDateLimit(null);
      setRunDateLimitTimeZone(null);
    }

    setCustomAttachments(schedule.info.custom_attachments || []);
    setProviderIsConstantContact(isConstantContact);
    setScheduleName(isClone ? "" : schedule.schedule_name || "");
    setAttachments(schedule.attachments || []);
    setSmtpConfigId(schedule.info.smtp_config_id || undefined);
    setProviderIsSMTP(schedule.email_service === CONSTANTS.EMAIL_SERVICES.SMTP);
    setIsSendingToAllContacts(shouldSendToAllContacts);
    setConstantContactAccountId(
      schedule.info.constant_contact_account_id || undefined
    );
    setMailgunDomainSelected(isMailgun ? mailgunDomainSelected : null);
    setMailgunDomainOptions(isMailgun ? mailgunDomainOptions : []);
    setSelectedEmailAttachments(schedule.info.email_attachment_ids || []);

    setCheckFromField(!isConstantContactOrSMTP);
  };

  const fetchCampaign = useCallback(async () => {
    const campaign = await CampaignService.getCampaign(campaignId);
    setSelectedTemplates(campaign.info.selected_templates || []);
  }, [campaignId]);

  const getEmailDeliveryTime = (dateTimeStr, timeZone) => {
    if (isClone) {
      return null;
    }
    if (dateTimeStr) {
      const tz = timeZone || "UTC";
      const localTime = moment.utc(dateTimeStr).tz(tz);
      return moment.utc(localTime.format("YYYY-MM-DD HH:mm:ss"));
    } else {
      return moment().utc().startOf("day");
    }
  };

  const setTemplateOptions = useCallback(() => {
    if (!templates) return;

    const templateOptions = templates
      .filter(t => t.value !== "any")
      .map(template => {
        let disabled = false;
        let description = null;

        if (provider) {
          disabled = templateDisabled(template, provider);
          description = disabled ? "Disabled for selected provider" : null;
        }

        if (!template.enabled) {
          disabled = true;
          description = "Empty template";
        }

        if (disabled && emailTemplateId === template.id) {
          setEmailTemplateId(null);
        }

        return {
          ...template,
          disabled,
          description,
        };
      });
    setEmailTemplateOptions(templateOptions);
  }, [templates, provider, emailTemplateId]);

  const fetchTemplates = useCallback(async () => {
    try {
      let templates = await EmailTemplateService.getEmailTemplatesForFilters();
      templates = templates.map(template => ({
        id: template.id,
        key: template.id,
        value: template.id,
        text: template.name,
        info: template.info,
        enabled: template.enabled,
      }));

      setTemplates(templates);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  }, []);

  const validCondition = useCallback(condition => {
    if (!condition) return false;

    if (
      !condition.filter_context &&
      !condition.filter_operator &&
      condition.filter_operand2 === undefined
    ) {
      return true;
    }

    if (!condition.filter_context || !condition.filter_operator) return false;
    if (condition.filter_operand2 === undefined) return false;
    if (condition.filter_operand2 === "") return false;
    return !(
      Array.isArray(condition.filter_operand2) &&
      condition.filter_operand2.length === 0
    );
  }, []);

  const addFilter = useCallback(() => {
    if (!isSendingToAllContacts) {
      setConditions(prevConditions => [...prevConditions, { id: uuidv4() }]);
    }
  }, [isSendingToAllContacts]);

  const updateConditions = useCallback((id, formData) => {
    setConditions(prevConditions =>
      prevConditions.map(condition =>
        condition.id === id ? { ...formData, id } : condition
      )
    );
  }, []);

  const fetchCampaignStatuses = async () => {
    let statuses = await CampaignService.getCampaignStatuses();
    let campaignStatusOptions = statuses.map(status => ({
      key: status.id,
      value: status.id,
      text: status.name,
    }));
    campaignStatusOptions = sortBy(campaignStatusOptions, "text");
    campaignStatusOptions.unshift({
      id: null,
      info: null,
      key: null,
      text: "None/Unset",
      value: null,
    });
    setCampaignStatusOptions(campaignStatusOptions);
  };

  const fetchTags = async model => {
    let tags = await TagService.getTags({ model: model });
    if (model == "contact") {
      setContactTagOptions(tags);
    } else if (model == "CampaignTargetCall") {
      setCallTagOptions(tags);
    } else {
      setEntityTagOptions(tags);
    }
  };

  const fetchEmailFooters = async () => {
    let footers = await EmailFooterService.getEmailFootersForFilters();
    let footerOptions = footers.map(footer => ({
      key: footer.id,
      value: footer.id,
      text: footer.name,
    }));
    footerOptions.unshift({ key: null, value: null, text: "None" });
    setFooterOptions(footerOptions);
  };

  const fetchEmailSignatures = async () => {
    let signature = await EmailSignatureService.getEmailSignaturesForFilters();
    let signatureOptions = signature.map(signature => ({
      key: signature.id,
      value: signature.id,
      text: signature.name,
    }));
    signatureOptions.unshift({ key: null, value: null, text: "None" });
    setSignatureOptions(signatureOptions);
  };

  const fetchUsers = async () => {
    const fetchedUsers = await UserService.getUsers();
    setUsers(fetchedUsers);
  };

  const uploadFiles = async () => {
    if (files !== undefined && files.length > 0) {
      await uploadManager.uploadFiles(files);
      return uploadManager.uploads.map(f => f.attachment);
    } else {
      return [];
    }
  };

  const handleFileProgress = files => {
    setFiles(files);
  };

  const handleFooterChange = async (e, { value }) => {
    let footerId = value;
    if (value == "") {
      footerId = null;
    }
    setFooterId(footerId);
  };

  const handleSignatureChange = async (e, { value }) => {
    let signatureId = value;
    if (value == "") {
      signatureId = null;
    }
    setSignatureId(signatureId);
  };

  const handleEmailAttachmentsChange = (e, { value }) => {
    setSelectedEmailAttachments(value || []);
  };

  const handleChange = (event, data) => {
    const value = data.type === "checkbox" ? data.checked : data.value;
    const name = data.name;
    // Update the specific state based on the field name
    switch (name) {
      case "status":
        setStatus(value);
        break;
      case "scheduleName":
        setScheduleName(value);
        break;
      case "fromAddress":
        setFromAddress(value);
        break;
      case "cc":
        setCc(value);
        break;
      case "bcc":
        setBcc(value);
        break;
      case "ccAccountManagers":
        setCcAccountManagers(value);
        break;
      case "ccExternalAccountManagers":
        setCcExternalAccountManagers(value);
        break;
      case "mailgunDomainSelected":
        setMailgunDomainSelected(value);
        break;
      case "sendToAllEmailAddresses":
        setSendToAllEmailAddresses(value);
        break;
      default:
        console.warn(`No state setter found for field: ${name}`);
    }
  };

  const handleSendingToAllContactsToggle = (event, data) => {
    const isChecked = data.checked;
    setIsSendingToAllContacts(isChecked);

    if (isChecked) {
      setConditions([]);
    } else {
      const newCondition = { id: uuidv4() };
      setConditions([newCondition]);
    }
  };

  const handleSelect = (e, { name, value }) => {
    switch (name) {
      case "constantContactAccountId":
        setConstantContactAccountId(value);
        break;
      default:
        console.warn(`No state setter found for field: ${name}`);
    }
  };

  const validateFields = async () => {
    let errors = {};

    if (checkFromField && !Validate.emailUsername(fromAddress)) {
      errors.fromAddress = 'Invalid value in "From" field';
    }
    if (!senderName) {
      errors.senderName = "Sender Name is required";
    }
    if (!scheduleName) {
      errors.scheduleName = "Schedule Name is required";
    }
    if (!sendNow && !emailDeliveryTime && !providerIsConstantContact) {
      errors.emailDeliveryTime =
        "Delivery Time & Date is required when Send Later is selected";
    }
    if (runUntil && !runDateLimit) {
      errors.runDateLimit =
        "Schedule Time & Date Limit is required when Run Until is selected";
    }
    return errors;
  };

  const handlePreSubmit = async () => {
    const errors = await validateFields();
    if (Object.keys(errors).length > 0) {
      setSubmitErrors(errors);
      throw new Error(
        typeof errors === "string"
          ? errors
          : Object.values(errors).join(", ") || "Validation failed"
      );
    }
    setSubmitErrors({});
    setFieldErrors({});

    setShowConfirmationModal(true);
  };

  const handleConfirmationClose = () => {
    setShowConfirmationModal(false);
  };

  const resolveEmailDeliveryTime = () => {
    const pickedTime = convertLocalDisplayToUTC(emailDeliveryTime, timeZone);
    // Constant Contact owns delivery, so the picker records when the email went
    // out rather than scheduling it. Left blank, it has not gone out yet.
    if (providerIsConstantContact) {
      return pickedTime || null;
    }
    if (sendNow) {
      return moment.utc();
    }
    return pickedTime || moment.utc();
  };

  const handleSubmit = async () => {
    try {
      setShowConfirmationModal(false);
      setIsSubmitting(true);
      setSubmitErrors({});
      setFieldErrors({});
      let submissionData = {
        status,
        time_zone: timeZone,
        sender_name: senderName,
        email_service: provider,
        schedule_name: scheduleName,
        conditions,
        email_template_id: emailTemplateId,
        attachments: await uploadFiles(),
        is_sending_to_all_contacts: isSendingToAllContacts,
        email_delivery_time: resolveEmailDeliveryTime(),
      };

      let finalFromAddress = fromAddress;
      if (provider === CONSTANTS.EMAIL_SERVICES.MAILGUN) {
        finalFromAddress = `${fromAddress}@${mailgunDomainSelected}`;
      } else if (providerIsConstantContact) {
        const selectedAccount = constantContactAccounts.find(
          account => account.value === constantContactAccountId
        );
        if (!selectedAccount?.disabled) {
          finalFromAddress = selectedAccount.text;
        }
      }

      submissionData.info = {
        from_address: finalFromAddress,
        cc,
        bcc,
        cc_account_managers: ccAccountManagers,
        cc_external_account_managers: ccExternalAccountManagers,
        custom_attachments: customAttachments,
        send_to_all_email_addresses: sendToAllEmailAddresses,
        smtp_config_id,
        constant_contact_account_id: constantContactAccountId,
        footer_id: footerId || null,
        signature_id: signatureId,
        email_attachment_ids: selectedEmailAttachments,
        send_now: sendNow,
        schedule_send_option: {
          run_once: runOnce,
          run_continuously: runContinuously,
          run_until: runUntil,
          run_date_limit: runDateLimit
            ? convertLocalDisplayToUTC(
                runDateLimit,
                runDateLimitTimeZone || timeZone
              ).format("YYYY-MM-DDTHH:mm:ss[Z]")
            : runDateLimit,
          run_date_limit_time_zone: runDateLimitTimeZone,
        },
      };

      if (providerIsConstantContact) {
        submissionData.info = {
          ...submissionData.info,
          sync_status: "pending",
          sync_error: null,
        };
      }

      if (scheduleId) {
        await CampaignScheduleService.editSchedule(
          campaignId,
          scheduleId,
          submissionData
        );
      } else {
        await CampaignScheduleService.createSchedule(
          campaignId,
          submissionData
        );
      }

      setIsSubmitting(false);
      if (onSuccess) {
        onSuccess();
      }
      if (onClose) {
        onClose();
      }
    } catch (error) {
      setIsSubmitting(false);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to save schedule";
      setSubmitErrors({ global: errorMessage });
      console.error("Submit error:", error);
      throw error;
    }
  };

  const handleDrop = files => {
    if (!uploadManager) {
      const newUploadManager = new UploadManager();
      newUploadManager.addProgressListener(handleFileProgress);
      setUploadManager(newUploadManager);
    }

    setFiles(prevFiles => {
      const existingFiles = prevFiles || [];
      return [...existingFiles, ...files];
    });
  };

  const deleteFile = fileIndex => {
    setFiles(prevFiles => {
      const newFiles = [...prevFiles];
      newFiles.splice(fileIndex, 1);
      return newFiles;
    });
  };

  const handleNameChange = event => {
    setSenderName(event.target.value);
  };

  const handleEmailDeliveryTimeChange = (field, value) => {
    switch (field) {
      case "sendNow":
        setSendNow(value);
        if (value) {
          setEmailDeliveryTime(getEmailDeliveryTime());
          setTimeZone(getUserTimeZone());
        }
        break;
      case "timeZone":
        // When timezone changes, adjust display to maintain same UTC time
        if (emailDeliveryTime && !sendNow) {
          const adjustedTime = adjustDisplayTimeForTimezoneChange(
            emailDeliveryTime,
            timeZone,
            value
          );
          setEmailDeliveryTime(adjustedTime);
        }
        setTimeZone(value);
        break;
      case "emailDeliveryTime":
        if (moment.isMoment(value)) {
          setEmailDeliveryTime(value);
        }
        break;
      default:
        console.warn(`No handler found for field: ${field}`);
    }
  };

  const checkAttachmentArray = obj => {
    if (obj.custom_attachment_uri) {
      return true;
    } else {
      return false;
    }
  };

  const checkIfCanValidate = useCallback(() => {
    const isConditionsFilled = isSendingToAllContacts
      ? true
      : conditions.length > 0;
    if (
      emailTemplateId &&
      isConditionsFilled &&
      customAttachments.every(checkAttachmentArray)
    ) {
      setValidateDisabled(false);
    } else {
      setValidateDisabled(true);
    }
  }, [isSendingToAllContacts, conditions, emailTemplateId, customAttachments]);

  const handleTemplateSelect = (e, data) => {
    setEmailTemplateId(data.value);
  };

  const addAttachment = () => {
    const newCustomAttachments = [...customAttachments, {}];
    setCustomAttachments(newCustomAttachments);
  };

  const removeAttachment = attachmentIdx => {
    const newCustomAttachments = [...customAttachments];
    newCustomAttachments.splice(attachmentIdx, 1);
    setCustomAttachments(newCustomAttachments);
  };

  const removeEmailAttachment = attachmentId => {
    setSelectedEmailAttachments(prev => prev.filter(id => id !== attachmentId));
  };

  const getSelectedEmailAttachmentDetails = () => {
    return selectedEmailAttachments.map(id => {
      const attachment = emailAttachmentOptions.find(
        option => option.value === id
      );
      return (
        attachment || { id, value: id, text: "Unknown", file_name: "Unknown" }
      );
    });
  };

  const renderAttachments = () => {
    const selectedEmailAttachmentDetails = getSelectedEmailAttachmentDetails();

    return (
      <>
        {attachments.length > 0 ? (
          <Form.Field label="Previous Attachments" />
        ) : null}
        <List relaxed>
          {attachments.length > 0
            ? attachments.map((file, i) => {
                return <List.Item key={i}>{file.file_name}</List.Item>;
              })
            : null}
        </List>

        {selectedEmailAttachmentDetails.length > 0 && (
          <>
            <Form.Field label="Email Attachments" />
            <List relaxed>
              {selectedEmailAttachmentDetails.map(attachment => (
                <List.Item key={`email-${attachment.value}`}>
                  <List.Content floated="right">
                    <Icon
                      name="delete"
                      onClick={() => removeEmailAttachment(attachment.value)}
                      style={{ cursor: "pointer" }}
                    />
                  </List.Content>
                  <Icon name="mail" />
                  {attachment.file_name || attachment.text}
                </List.Item>
              ))}
            </List>
          </>
        )}

        {attachments.length > 0 || selectedEmailAttachmentDetails.length > 0 ? (
          <Form.Field label="New File Attachments" />
        ) : (
          <Form.Field label="File Attachments" />
        )}

        {isSubmitting && files && files.every(f => f.file) ? (
          <UploadProgressRing uploads={files} />
        ) : (
          <List relaxed>
            {files
              ? files.map((file, i) =>
                  file !== undefined ? (
                    <List.Item key={i}>
                      <List.Content floated="right">
                        <Icon name="delete" onClick={() => deleteFile(i)} />
                      </List.Content>
                      <Icon name="file" />
                      {file.name}
                    </List.Item>
                  ) : null
                )
              : null}
          </List>
        )}
        <Dropzone onDrop={files => handleDrop(files)}>
          {({ getRootProps, getInputProps }) => (
            <Segment style={{ border: "dashed 2px grey", textAlign: "center" }}>
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <p>Drag 'n' drop some files here, or click to select files</p>
              </div>
            </Segment>
          )}
        </Dropzone>
        {customAttachments.map((attachment, attachmentIdx) => (
          <CustomAttachmentForm
            key={attachmentIdx}
            attachmentIdx={attachmentIdx}
            attachment={attachment}
            removeAttachment={removeAttachment}
            customAttachmentList={customAttachments}
            scheduleConditions={conditions}
            emailTemplateId={emailTemplateId}
            campaignId={campaignId}
            scheduleId={scheduleId}
            validateDisabled={validateDisabled}
            checkIfCanValidate={checkIfCanValidate}
            sendToAllEmailAddresses={sendToAllEmailAddresses}
          />
        ))}
        <Button
          basic
          color="grey"
          size="mini"
          content="Add Custom Attachment"
          type="button"
          onClick={() => addAttachment()}
        />
      </>
    );
  };

  const removeFilter = useCallback(
    conditionIdx => {
      if (!isSendingToAllContacts) {
        setConditions(prevConditions => {
          const newConditions = [...prevConditions];
          newConditions.splice(conditionIdx, 1);
          return newConditions;
        });
      }
    },
    [isSendingToAllContacts]
  );

  const makeFilterForm = useCallback(
    (condition, conditionIdx) => {
      return (
        <OutreachFiltersForm
          key={condition.id}
          uuid={condition.id}
          campaignStatuses={campaignStatusOptions}
          emailTemplateOptions={templates}
          userOptions={userOptions}
          campaignId={campaignId}
          updateConditions={updateConditions}
          condition={condition}
          contactTagOptions={contactTagOptions}
          entityTagOptions={entityTagOptions}
          callTagsOptions={callTagOptions}
          conditionIdx={conditionIdx}
          removeFilterElement={removeFilter}
          checkIfCanValidate={checkIfCanValidate}
          emailDomainFilter={true}
          conditions_list={conditions}
          isEmailScheduler={true}
        />
      );
    },
    [
      campaignStatusOptions,
      templates,
      userOptions,
      campaignId,
      updateConditions,
      contactTagOptions,
      entityTagOptions,
      callTagOptions,
      removeFilter,
      checkIfCanValidate,
      conditions,
    ]
  );

  const ConditionsRender = (
    <>
      <Form.Field label="Conditions" required style={{ marginTop: "10px" }} />
      <div className="and-filter">
        {conditions.map((condition, conditionIdx) => {
          if (emailTemplateOptions.length > 0) {
            return makeFilterForm(condition, conditionIdx);
          } else {
            return null;
          }
        })}
      </div>
      {!isSendingToAllContacts && (
        <Button
          basic
          color="grey"
          size="mini"
          content="Add Filter"
          name="fromAddress"
          onClick={addFilter}
          type="button"
        />
      )}
    </>
  );

  let usernameField = (
    <>
      <Form.Input
        label="From"
        placeholder="ex. username"
        onChange={handleChange}
        name="fromAddress"
        value={fromAddress}
        error={!!errorMessages.fromAddress}
      />
      <div style={{ paddingTop: "2rem", marginLeft: "-1rem" }}>
        <p>{`@${fromDomain}`}</p>
      </div>
    </>
  );

  if (providerIsSMTP) {
    usernameField = (
      <Form.Select
        selectOnBlur={false}
        selection
        label="Choose Account"
        options={smtpOptions}
        value={smtp_config_id}
        onChange={handleAccountChange}
        required
      />
    );
  } else if (provider === CONSTANTS.EMAIL_SERVICES.MAILGUN) {
    usernameField = (
      <>
        <Form.Input
          required
          label="From"
          placeholder="ex. username"
          onChange={handleChange}
          name="fromAddress"
          value={fromAddress}
          error={!!errorMessages.fromAddress}
        />

        <Form.Select
          selectOnBlur={false}
          name="mailgunDomainSelected"
          selection
          label="Select a domain"
          options={mailgunDomainOptions}
          onChange={handleChange}
          value={mailgunDomainSelected}
          required
        />
      </>
    );
  }

  const filteredEmailTemplateOptions =
    selectedTemplates && selectedTemplates.length > 0
      ? emailTemplateOptions.filter(option =>
          selectedTemplates.includes(option.value)
        )
      : emailTemplateOptions;

  const isReady = () => {
    const hasBasicFields =
      !!scheduleName && !!provider && !!senderName && !!emailTemplateId;
    if (!hasBasicFields) return false;

    let hasValidFromAddress = true;
    if (provider) {
      if (providerIsSMTP) {
        hasValidFromAddress = !!smtp_config_id;
      } else if (providerIsMailgun) {
        hasValidFromAddress = !!fromAddress && !!mailgunDomainSelected;
      } else if (providerIsConstantContact) {
        hasValidFromAddress = !!constantContactAccountId;
      } else {
        hasValidFromAddress = !!fromAddress;
      }
    }
    if (!hasValidFromAddress) return false;

    if (!sendNow && !emailDeliveryTime && !providerIsConstantContact) {
      return false;
    }

    if (runUntil && !runDateLimit) {
      return false;
    }

    if (isSendingToAllContacts) {
      return true; // No need to check filters if sending to all contacts
    } else {
      return (
        conditions.length > 0 &&
        conditions.every(cond => {
          return (
            cond &&
            cond.filter_context &&
            cond.filter_operator &&
            cond.filter_operand2 !== undefined &&
            cond.filter_operand2 !== "" &&
            !(
              Array.isArray(cond.filter_operand2) &&
              cond.filter_operand2.length === 0
            )
          );
        })
      );
    }
  };

  const handleProviderChange = async (event, data) => {
    const isConstantContact =
      data.value === CONSTANTS.EMAIL_SERVICES.CONSTANT_CONTACT;
    const isSMTP = data.value === CONSTANTS.EMAIL_SERVICES.SMTP;
    const isMailgun = data.value === CONSTANTS.EMAIL_SERVICES.MAILGUN;
    const isConstantContactOrSMTP = isConstantContact || isSMTP;

    // Constant Contact never sends immediately from here, so "Send Now" is not
    // a state it can be in. Only reset when that changes: resetting on every
    // provider change would discard a "Send Later" date already picked on
    // another provider, and leaving it alone would strand the form demanding a
    // delivery time it no longer shows.
    if (isConstantContact !== providerIsConstantContact) {
      setSendNow(!isConstantContact);
      // The picker records when the email went out for Constant Contact and
      // when it should go out everywhere else, so a value carried across that
      // boundary is never the right one.
      setEmailDeliveryTime(null);
    }

    if (isMailgun) {
      const { domainOptions, defaultDomain } = await fetchMailgunDomains();
      setProvider(data.value);
      setProviderIsConstantContact(isConstantContact);
      setProviderIsSMTP(isSMTP);
      setProviderIsMailgun(true);
      setFromAddress("");
      setCheckFromField(!isConstantContactOrSMTP);
      setMailgunDomainOptions(domainOptions);
      setMailgunDomainSelected(defaultDomain);
    } else {
      setProvider(data.value);
      setProviderIsConstantContact(isConstantContact);
      setProviderIsSMTP(isSMTP);
      setProviderIsMailgun(false);
      setFromAddress("");
      setCheckFromField(!isConstantContactOrSMTP);
      setMailgunDomainOptions([]);
      setMailgunDomainSelected(null);
    }
    await fetchEmailServicesOptions(data.value);

    handleAccountChange(null, { value: smtp_config_id });
  };

  const ready = isReady();

  useEffect(() => {
    checkIfCanValidate();
  }, [conditions, isSendingToAllContacts, validCondition, checkIfCanValidate]);

  useEffect(() => {
    if (templates && templates.length > 0) {
      setTemplateOptions();
    }
  }, [templates, setTemplateOptions]);

  useEffect(() => {
    let isMounted = true;

    const initializeForm = async () => {
      if (isMounted) {
        if (campaignId && scheduleId) {
          await fetchSchedule(campaignId, scheduleId, isClone);
        }

        fetchTags("contact");
        fetchTags("entity");
        fetchTags("CampaignTargetCall");
        fetchTemplates();
        fetchCampaignStatuses();
        fetchCampaign();
        setupTimezones();
        fetchEmailServices();
        fetchEmailFooters();
        fetchEmailSignatures();
        fetchUsers();
        fetchEmailAttachments();
      }
    };

    initializeForm();

    return () => {
      isMounted = false;
    };
  }, [campaignId, scheduleId, isClone]);

  const handleScheduleOptionChange = useCallback(option => {
    switch (option) {
      case "continuous":
        setRunContinuously(true);
        setRunOnce(false);
        setRunUntil(false);
        setRunDateLimit(null);
        setRunDateLimitTimeZone(null);
        break;
      case "once":
        setRunContinuously(false);
        setRunOnce(true);
        setRunUntil(false);
        setRunDateLimit(null);
        setRunDateLimitTimeZone(null);
        break;
      case "date":
        setRunContinuously(false);
        setRunOnce(false);
        setRunUntil(true);
        break;
    }
  }, []);

  const handleRunDateLimitChange = useCallback(value => {
    if (moment.isMoment(value)) {
      setRunDateLimit(value);
    }
  }, []);

  const handleRunDateLimitTimeZoneChange = useCallback(
    value => {
      // When timezone changes, adjust display to maintain same UTC time
      if (runDateLimit) {
        const adjustedTime = adjustDisplayTimeForTimezoneChange(
          runDateLimit,
          runDateLimitTimeZone || timeZone,
          value
        );
        setRunDateLimit(adjustedTime);
      }
      setRunDateLimitTimeZone(value);
    },
    [runDateLimit, runDateLimitTimeZone, timeZone]
  );

  const getDeliveryTimeMode = () => {
    return sendNow ? "Send Now" : "Send Later";
  };

  const getDeliveryTimeDisplay = () => {
    if (sendNow) {
      return "Send Now";
    }
    if (!emailDeliveryTime) {
      return null;
    }
    const formattedTime = emailDeliveryTime.format("YYYY-MM-DD hh:mm A");
    return `${formattedTime} (${timeZone})`;
  };

  const getScheduleOptionMode = () => {
    if (runContinuously) return "Run Continuously";
    if (runOnce) return "Run Once";
    if (runUntil) return "Run Until";
    return "Not set";
  };

  const getRunUntilDisplay = () => {
    if (!runUntil || !moment.isMoment(runDateLimit)) {
      return null;
    }
    const effectiveTimeZone = runDateLimitTimeZone || timeZone;
    const formattedTime = runDateLimit.format("YYYY-MM-DD hh:mm A");
    return `${formattedTime} (${effectiveTimeZone})`;
  };

  const getRecipientsMode = () => {
    if (isSendingToAllContacts) {
      return "All Contacts";
    }
    return "Filtered";
  };

  const formatFilterOperand1 = operand1 => {
    const operandLabels = {
      last_delivered_at: "Email Last Delivered At",
      last_opened_at: "Email Last Opened At",
      last_clicked_at: "Email Last Clicked At",
      last_delivered_template: "Last Delivered Template",
      call_status: "Last Call Status",
      last_form_response_date: "Last Form Response Date",
      last_called_date: "Last Called Date",
      days_since_last_called: "Days Since Last Called",
      days_since_last_delivered: "Email Days Since Last Delivered",
      number_of_times_called: "Number of Times Called",
      tags: "Contact Tags",
      campaign_status: "Campaign Status",
      assigned_user_id: "Assignee",
      entity_tags: "Entity Tags",
      call_tags: "Call Tags",
      email_domain: "Email Domain",
    };
    return operandLabels[operand1] || operand1;
  };

  const formatFilterOperator = operator => {
    const operatorLabels = {
      contains: "contains",
      excludes: "excludes",
      eq: "equals",
      gt: "greater than",
      ge: "greater than or equal to",
      lt: "less than",
      le: "less than or equal to",
      exactly: "exactly",
      between: "between",
      before: "before",
      after: "after",
      is: "is",
    };
    return operatorLabels[operator] || operator;
  };

  const getOptionsForOperand = operand1 => {
    switch (operand1) {
      case "tags":
        return contactTagOptions;
      case "entity_tags":
        return entityTagOptions;
      case "call_tags":
        return callTagOptions;
      case "campaign_status":
        return campaignStatusOptions;
      case "assigned_user_id":
        return userOptions;
      case "last_delivered_template":
        return templates;
      default:
        return null;
    }
  };

  const formatFilterValue = (operand1, value) => {
    if (!value) return "";

    const options = getOptionsForOperand(operand1);
    if (!options) {
      if (Array.isArray(value)) {
        const formatted = value.map(item => {
          if (typeof item === "object" && item !== null) {
            // Handle date objects: {from, to} or {from}
            if (item.from && item.to) {
              return `${item.from} to ${item.to}`;
            }
            if (item.from) {
              return item.from;
            }
            return JSON.stringify(item);
          }
          return item;
        });
        return formatted.length > 2
          ? `${formatted.length} items`
          : formatted.join(", ");
      }
      return value;
    }

    const getLabels = ids => {
      const idArray = Array.isArray(ids) ? ids : [ids];
      const labels = idArray.map(id => {
        const option = options.find(
          opt => opt.value === id || opt.id === id || opt.key === id
        );
        return option ? option.text || option.name : id;
      });
      return labels.length > 2 ? `${labels.length} items` : labels.join(", ");
    };

    return getLabels(value);
  };

  const formatFilterContext = context => {
    return context === "entity" ? "Entity" : "Contact";
  };

  const getFiltersSummary = () => {
    if (!conditions || conditions.length === 0) {
      return [];
    }

    return conditions
      .filter(cond => cond && cond.filter_operand1 && cond.filter_operator)
      .map(cond => {
        const context = formatFilterContext(cond.filter_context);
        const field = formatFilterOperand1(cond.filter_operand1);
        const operator = formatFilterOperator(cond.filter_operator);
        const value = formatFilterValue(
          cond.filter_operand1,
          cond.filter_operand2
        );

        return `${context} ${field} ${operator} ${value || ""}`.trim();
      });
  };

  return (
    <>
      <ScheduleConfirmationModal
        open={showConfirmationModal}
        onClose={handleConfirmationClose}
        onConfirm={handleSubmit}
        campaignId={campaignId}
        deliveryTimeMode={getDeliveryTimeMode()}
        deliveryTimeDisplay={getDeliveryTimeDisplay()}
        scheduleOptionMode={getScheduleOptionMode()}
        runUntilDisplay={getRunUntilDisplay()}
        recipientsMode={getRecipientsMode()}
        filtersSummary={getFiltersSummary()}
        conditions={conditions}
        emailTemplateId={emailTemplateId}
        isSendingToAllContacts={isSendingToAllContacts}
        sendToAllEmailAddresses={sendToAllEmailAddresses}
      />
      <RuvixxForm
        ready={ready}
        onSubmit={handlePreSubmit}
        onCancel={onClose}
        error={Object.keys(errorMessages).length > 0}
        errorMessage={Object.values(errorMessages).find(Boolean) || ""}
        extraButtons={[
          <EmailableContactsModal
            key={"emailable_contacts_modal"}
            disabled={!ready}
            campaignId={campaignId}
            data={{
              conditions: conditions,
              email_template_id: emailTemplateId,
              sending_to_all_contacts: isSendingToAllContacts,
              info: {
                send_to_all_email_addresses: sendToAllEmailAddresses,
              },
            }}
          />,
        ]}
        successHeader="Email Schedule Created"
        successMessage="Successfully created a new schedule"
      >
        {!providerIsConstantContact && (
          <Form.Checkbox
            toggle
            name="status"
            checked={status}
            onChange={handleChange}
            label={`Status [${status ? "Active" : "Inactive"}]`}
            required
          />
        )}
        <Form.Input
          name="scheduleName"
          value={scheduleName}
          onChange={handleChange}
          label="Schedule Name"
          placeholder="Type a unique Schedule Name"
          required
          error={!!errorMessages.scheduleName}
        />
        <Form.Group>
          <Form.Select
            selectOnBlur={false}
            selection
            label="Email Provider"
            placeholder="Select an Email Provider"
            options={emailServices}
            onChange={handleProviderChange}
            value={provider}
            required
          />
          {providerIsConstantContact && (
            <Form.Select
              selectOnBlur={false}
              selection
              name={"constantContactAccountId"}
              label="Constant Contact Account"
              placeholder="Select Account"
              options={constantContactAccounts}
              onChange={handleSelect}
              value={constantContactAccountId}
              required
            />
          )}
        </Form.Group>
        {!providerIsConstantContact && (
          <>
            <Form.Group widths="equal">
              <Form.Input
                required
                label="Sender Name"
                placeholder="Type in Sender Name"
                onChange={handleNameChange}
                name="senderName"
                value={senderName}
                error={!!errorMessages.senderName}
              />
              {usernameField}
            </Form.Group>
            <Accordion active={ccMenuOpen} as={Form.Field}>
              <Accordion.Title
                active={ccMenuOpen}
                index={0}
                onClick={() => setCcMenuOpen(!ccMenuOpen)}
              >
                <Icon name="dropdown" />
                CC/BCC Options
              </Accordion.Title>
              <Accordion.Content active={ccMenuOpen}>
                <Form.Group>
                  <Form.Checkbox
                    toggle
                    name="ccAccountManagers"
                    checked={ccAccountManagers}
                    onChange={handleChange}
                    label="CC Account Managers"
                  />
                  <Form.Checkbox
                    toggle
                    name="ccExternalAccountManagers"
                    checked={ccExternalAccountManagers}
                    onChange={handleChange}
                    label="CC External Account Managers"
                  />
                </Form.Group>
                <Form.Input
                  label="CC"
                  placeholder="email1@test.com,email2@test.com"
                  onChange={handleChange}
                  name="cc"
                  value={cc}
                />
                <Form.Input
                  label="BCC"
                  placeholder="email1@test.com,email2@test.com"
                  onChange={handleChange}
                  name="bcc"
                  value={bcc}
                />
              </Accordion.Content>
            </Accordion>
          </>
        )}
        <Grid
          columns="equal"
          style={{ paddingLeft: "0.5em", paddingBottom: "2em" }}
        >
          <Grid.Row>
            <Grid.Column>
              <EmailDeliveryTime
                // DateTimePicker mirrors the parent value only while it is
                // truthy, so clearing the date on a provider switch leaves it
                // still displayed. Remounting on the Constant Contact boundary
                // drops that retained value with it.
                key={
                  providerIsConstantContact ? "constant-contact" : "scheduled"
                }
                timeZone={timeZone}
                timeZones={timeZones}
                sendNow={sendNow}
                emailDeliveryTime={emailDeliveryTime}
                setEmailDeliveryTime={handleEmailDeliveryTimeChange}
                error={!!errorMessages.emailDeliveryTime}
                showSendNowOptions={!providerIsConstantContact}
                required={!providerIsConstantContact}
              />
            </Grid.Column>
            {!providerIsConstantContact && (
              <Grid.Column>
                <EmailScheduleSendOptionForm
                  timeZone={timeZone}
                  timeZones={timeZones}
                  runOnce={runOnce}
                  runUntil={runUntil}
                  runContinuously={runContinuously}
                  runDateLimit={runDateLimit}
                  runDateLimitTimeZone={runDateLimitTimeZone}
                  onScheduleOptionChange={handleScheduleOptionChange}
                  onRunDateLimitChange={handleRunDateLimitChange}
                  onRunDateLimitTimeZoneChange={
                    handleRunDateLimitTimeZoneChange
                  }
                  error={!!errorMessages.runDateLimit}
                />
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
        <Form.Group widths="equal">
          <Form.Select
            selectOnBlur={false}
            required
            search
            options={filteredEmailTemplateOptions}
            name="email_template_id"
            placeholder="Select an Email Template"
            value={emailTemplateId}
            label="Email Template"
            onChange={handleTemplateSelect}
          />
          <Form.Select
            selectOnBlur={false}
            search
            clearable
            label="Email Footer"
            placeholder="Select an Email Footer"
            value={footerId}
            onChange={handleFooterChange}
            options={footerOptions}
          />
          <Form.Select
            selectOnBlur={false}
            search
            clearable
            label="Email Signature"
            placeholder="Select an Email Signature"
            value={signatureId}
            onChange={handleSignatureChange}
            options={signatureOptions}
          />
        </Form.Group>

        {!providerIsConstantContact && (
          <Form.Select
            multiple
            search
            clearable
            label="Email Attachments"
            placeholder="Select Email Attachments"
            value={selectedEmailAttachments}
            onChange={handleEmailAttachmentsChange}
            options={emailAttachmentOptions}
          />
        )}

        {!providerIsConstantContact && renderAttachments()}

        <Form.Checkbox
          toggle
          name="sendToAllEmailAddresses"
          checked={sendToAllEmailAddresses}
          onChange={handleChange}
          label={`Send to all enabled emails addresses per contact [${
            sendToAllEmailAddresses ? "Active" : "Inactive"
          }]`}
          required
        />
        <Form.Checkbox
          toggle
          name="isSendingToAllContacts"
          checked={isSendingToAllContacts}
          onChange={handleSendingToAllContactsToggle}
          label={`Send to all contacts [${
            isSendingToAllContacts ? "Active" : "Inactive"
          }]`}
          required
        />
        {!isSendingToAllContacts ? ConditionsRender : null}
      </RuvixxForm>
    </>
  );
};

CampaignScheduleForm.propTypes = {
  campaignId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  newSchedule: PropTypes.bool,
  scheduleId: PropTypes.number,
  isClone: PropTypes.bool,
};

export default CampaignScheduleForm;
